import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings as SettingsIcon } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const STORAGE_KEY = "sxg-consent";
const STORAGE_VERSION = 1;

const setConsent = (consent) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...consent, _v: STORAGE_VERSION, _t: Date.now() })
    );
  } catch {
    // ignore
  }
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", consent);
  }
};

const ALL_GRANTED = {
  ad_storage: "granted",
  ad_user_data: "granted",
  ad_personalization: "granted",
  analytics_storage: "granted",
};

const ALL_DENIED = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
};

export const CookieBanner = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (!saved) setVisible(true);
    } catch {
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    setConsent(ALL_GRANTED);
    setVisible(false);
  };

  const handleRefuseAll = () => {
    setConsent(ALL_DENIED);
    setVisible(false);
  };

  const handleSavePreferences = () => {
    setConsent({
      analytics_storage: analytics ? "granted" : "denied",
      ad_storage: marketing ? "granted" : "denied",
      ad_user_data: marketing ? "granted" : "denied",
      ad_personalization: marketing ? "granted" : "denied",
    });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6 lg:p-8 pointer-events-none"
    >
      <div className="max-w-3xl mx-auto pointer-events-auto bg-[#0d0c0b] border border-[#b87333]/40 shadow-[0_-20px_60px_rgba(0,0,0,0.8)] backdrop-blur">
        {!showSettings ? (
          <div className="p-5 sm:p-7">
            <div className="flex items-start gap-3 mb-4">
              <Cookie className="w-5 h-5 text-[#d89557] flex-shrink-0 mt-1" strokeWidth={1.5} />
              <h3 className="font-display text-lg sm:text-xl text-[#f4ede0]">
                {t("cookies.title")}
              </h3>
            </div>
            <p className="text-sm text-[#cbc1ad] leading-relaxed mb-5">
              {t("cookies.intro")}{" "}
              <Link
                to="/privacy"
                className="text-[#d89557] underline underline-offset-2 hover:text-[#f4ede0] transition"
              >
                {t("cookies.linkPrivacy")}
              </Link>
              {" · "}
              <Link
                to="/cookies"
                className="text-[#d89557] underline underline-offset-2 hover:text-[#f4ede0] transition"
              >
                {t("cookies.linkCookies")}
              </Link>
            </p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                data-testid="cookie-refuse"
                onClick={handleRefuseAll}
                className="flex-1 px-5 py-3 text-xs uppercase tracking-[0.22em] border border-[#2a2422] text-[#cbc1ad] hover:border-[#b87333]/50 hover:text-[#f4ede0] transition"
              >
                {t("cookies.refuse")}
              </button>
              <button
                data-testid="cookie-settings"
                onClick={() => {
                  setAnalytics(false);
                  setMarketing(false);
                  setShowSettings(true);
                }}
                className="flex-1 px-5 py-3 text-xs uppercase tracking-[0.22em] border border-[#2a2422] text-[#cbc1ad] hover:border-[#b87333]/50 hover:text-[#f4ede0] transition inline-flex items-center justify-center gap-2"
              >
                <SettingsIcon className="w-3.5 h-3.5" />
                {t("cookies.customize")}
              </button>
              <button
                data-testid="cookie-accept"
                onClick={handleAcceptAll}
                className="flex-1 btn-copper px-5 py-3 text-xs uppercase tracking-[0.22em]"
              >
                {t("cookies.acceptAll")}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-7">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-lg sm:text-xl text-[#f4ede0]">
                {t("cookies.preferencesTitle")}
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-[#7a716a] hover:text-[#f4ede0] transition"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <PrefRow
                title={t("cookies.essentialTitle")}
                desc={t("cookies.essentialDesc")}
                checked={true}
                disabled={true}
                badge={t("cookies.required")}
                onChange={() => {}}
              />
              <PrefRow
                title={t("cookies.analyticsTitle")}
                desc={t("cookies.analyticsDesc")}
                checked={analytics}
                onChange={() => setAnalytics((v) => !v)}
                testid="pref-analytics"
              />
              <PrefRow
                title={t("cookies.marketingTitle")}
                desc={t("cookies.marketingDesc")}
                checked={marketing}
                onChange={() => setMarketing((v) => !v)}
                testid="pref-marketing"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={handleRefuseAll}
                className="flex-1 px-5 py-3 text-xs uppercase tracking-[0.22em] border border-[#2a2422] text-[#cbc1ad] hover:border-[#b87333]/50 hover:text-[#f4ede0] transition"
              >
                {t("cookies.refuse")}
              </button>
              <button
                data-testid="cookie-save-prefs"
                onClick={handleSavePreferences}
                className="flex-1 btn-copper px-5 py-3 text-xs uppercase tracking-[0.22em]"
              >
                {t("cookies.savePreferences")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const PrefRow = ({ title, desc, checked, disabled, badge, onChange, testid }) => (
  <label
    className={`flex items-start gap-4 p-4 border border-[#2a2422] bg-[#0a0a0a] ${
      disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer hover:border-[#b87333]/40"
    } transition`}
  >
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      data-testid={testid}
      className="mt-1 w-4 h-4 accent-[#b87333] flex-shrink-0"
    />
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="text-sm font-medium text-[#f4ede0]">{title}</span>
        {badge && (
          <span className="text-[9px] uppercase tracking-[0.2em] text-[#d89557] border border-[#b87333]/40 px-2 py-0.5">
            {badge}
          </span>
        )}
      </div>
      <p className="text-xs text-[#cbc1ad] leading-relaxed">{desc}</p>
    </div>
  </label>
);
